import { query } from '@angular/animations';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
const io = require('socket.io-client');
import { environment } from 'src/environments/environment';
import { AuthService } from './auth.service';

@Injectable({
  providedIn: 'root'
})
export class SocketIoService {

  socket;
  private hasRegisteredCoreListeners = false;
  private pendingPairDoneHandler?: (data: any) => void;
  private gatewayUpdatesZoneId?: string;
  private sensorValueMessages = new BehaviorSubject<any>(null);
  get sensorValueMessages$(): Observable<any> {
    return this.sensorValueMessages.asObservable();
  }

  private sensorUpdatedMessages = new BehaviorSubject<any>(null);
  get sensorUpdatedMessages$(): Observable<any> {
    return this.sensorUpdatedMessages.asObservable();
  }

  private ruleMessages = new BehaviorSubject<any>(null);
  get ruleMessages$(): Observable<any> {
    return this.ruleMessages.asObservable();
  }

  private logValueMessages = new BehaviorSubject<any>(null);
  get logValueMessages$(): Observable<any> {
    return this.logValueMessages.asObservable();
  }

  private notifications = new BehaviorSubject<any>(null);
  get notifications$(): Observable<any> {
    return this.notifications.asObservable();
  }

  didJoin = false;

  constructor(private authService: AuthService) {
    this.setupSocketConnection(() => {
      this.joinZoneUpdates();
      if (this.didJoin) {
        return
      }
      this.didJoin = true;
      // Only register these once, otherwise repeated setupSocketConnection() calls
      // can add duplicate listeners and confusing behavior.
      if (!this.hasRegisteredCoreListeners) {
        this.hasRegisteredCoreListeners = true;
        this.listenSensorValue();
        this.listenLogValue();
        this.listenNotification();
        this.listenSensorUpdated();
        this.listenRuleUpdates();
      }
    })
  }

  setupSocketConnection(callback) {
    // If we already have a live socket, reuse it.
    // Pairing currently calls setupSocketConnection() again, which (before this)
    // was creating a *second* socket and overwriting `this.socket`.
    if (this.socket && this.socket.connected) {
      callback();
      return;
    }

    const token = this.authService.getToken(false, false);
    this.socket = io(environment.apiEndpoint, {
      query: { token: token },
      // Prefer websockets (avoid long-polling -> upgrade churn) and allow fallback.
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionAttempts: Infinity,
      reconnectionDelay: 500,
      reconnectionDelayMax: 5000,
      timeout: 20000
    });
    this.socket.on('connect', () => {
      console.log('socket connected');

      // Rooms are not preserved across reconnects.
      // Re-join the last gateway-updates room so we don't miss one-shot events like `pair-done`.
      if (this.gatewayUpdatesZoneId) {
        // eslint-disable-next-line no-console
        console.log('[socket] re-join-gateway-updates', this.gatewayUpdatesZoneId);
        this.socket.emit('join-gateway-updates', this.gatewayUpdatesZoneId);
      }

      callback();
    });
    // socket.io provides a reason string
    this.socket.on('disconnect', (reason) => {
      console.log('socket disconnected', reason);

      // Helpful when the server drops us or when the client forces a disconnect.
      // eslint-disable-next-line no-console
      console.warn('[socket] disconnect reason:', reason);
    })

    this.socket.on('connect_error', (err) => {
      console.warn('[socket] connect_error', err?.message || err, err);
    });

    this.socket.on('reconnect_attempt', (attempt) => {
      console.warn('[socket] reconnect_attempt', attempt);
    });

    this.socket.on('reconnect', (attempt) => {
      console.warn('[socket] reconnect', attempt);
    });

    this.socket.on('reconnect_failed', () => {
      console.warn('[socket] reconnect_failed');
    });
  }

  closeSocketConnection() {
    // Debug helper: identify who is closing the socket.
    // (This is intentionally noisy until we find the culprit.)
    try {
      const err = new Error('closeSocketConnection called');
      // Stack traces are the fastest way to find the caller across dialogs/components.
      // eslint-disable-next-line no-console
      console.warn('[socket] closeSocketConnection()', err.stack);
    } catch (_) {
      // ignore
    }

    this.socket?.disconnect();
  }

  /**
   * Force the Socket.IO client to reconnect using the latest auth token.
   * Call this after login/logout/company switch so `socket.user` is re-derived
   * from the current JWT.
   */
  resetConnection() {
    try {
      if (this.socket) {
        try {
          this.socket.removeAllListeners?.();
        } catch (_) {
          // ignore
        }
        this.socket.disconnect();
      }
    } finally {
      this.socket = undefined;
      this.hasRegisteredCoreListeners = false;
      this.pendingPairDoneHandler = undefined;
      this.gatewayUpdatesZoneId = undefined;
      this.didJoin = false;
    }
  }

  private joinZoneUpdates() {
    this.socket.emit('join-zone-updates');
  }

  joinGatewayUpdates(zoneId: string) {
    this.gatewayUpdatesZoneId = zoneId;
    // eslint-disable-next-line no-console
    console.log('[socket] join-gateway-updates', zoneId, 'connected=', !!this.socket?.connected);

    // If we emit before the initial connect finishes, the event can be lost.
    // We already re-emit on each connect using `gatewayUpdatesZoneId`.
    if (this.socket?.connected) {
      this.socket.emit('join-gateway-updates', zoneId);
      // eslint-disable-next-line no-console
      console.log('[socket] join-gateway-updates emitted');
    } else {
      // eslint-disable-next-line no-console
      console.log('[socket] join-gateway-updates deferred until connect');
    }
  }

  private listenSensorValue() {
    this.socket.on('sensor-value', (data: any) => {
      this.sensorValueMessages.next(data);
    });
  }

  private listenLogValue() {
    this.socket.on('action-log', (data: any) => {

      this.logValueMessages.next(data);
    })
  }


  private listenNotification() {
    this.socket.on('new-notification', (data: any) => {

      this.notifications.next(data);
    })
  }

  private listenSensorUpdated() {
    this.socket.on('sensor-updated', (data: any) => {

      this.sensorUpdatedMessages.next(data);
    });
  }

  private listenRuleUpdates() {

    this.socket.on('rule-created', (data: any) => {
      if (data == null) {
        return;
      }
      console.log('rule-created', data);
      this.ruleMessages.next(data);
    });

    this.socket.on('rule-updated', (data: any) => {
      if (data == null) {
        return;
      }
      console.log('rule-updated', data);
      this.ruleMessages.next(data);
    });

    this.socket.on('rule-deleted', (data: any) => {
      if (data == null) {
        return;
      }
      console.log('rule-deleted', data);
      this.ruleMessages.next(data);
    });
  }

  listenPairingDone(callback) {
    // Make this subscription safe across repeated dialog opens:
    // remove any previous pending handler, then register a one-shot listener.
    if (this.pendingPairDoneHandler) {
      this.socket.off('pair-done', this.pendingPairDoneHandler);
      this.pendingPairDoneHandler = undefined;
    }

    this.pendingPairDoneHandler = (data: any) => {
      // eslint-disable-next-line no-console
      console.log('[socket] pair-done received', data);
      this.pendingPairDoneHandler = undefined;
      callback(data);
    };

    // eslint-disable-next-line no-console
    console.log('[socket] listening for pair-done');
    this.socket.once('pair-done', this.pendingPairDoneHandler);

    // Debug/backwards-compat:
    // Some deployments historically emitted pairing completion under a different topic.
    // Keep this as a non-breaking listener that only logs (does not callback).
    // eslint-disable-next-line no-console
    const debugTopics = ['pair-found', 'pairing-done', 'gateway-pair-done'];
    debugTopics.forEach((t) => {
      this.socket.once(t, (data: any) => {
        // eslint-disable-next-line no-console
        console.warn(`[socket] received unexpected pairing topic: ${t}`, data);
      });
    });
  }

  listenCalibrateStatus(callback) {
    this.socket.on('pair-done', (data: any) => {
      callback(data);
    });
  }


}
